import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, RotateCcw, Briefcase, Sparkles, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import FactoryHypeContactModal from "../components/FactoryHypeContactModal";
import VoiceRecorder from "../components/VoiceRecorder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_ID = "factoryhype_v2";

// Lisa - Teamleiterin avatar (v2 persona)
const BOT_AVATAR = "https://static.prod-images.emergentagent.com/jobs/a1355f58-fa33-4368-994e-884784b84e2d/images/35ff361465372cb2a15bf2f9d4a2b4c726defacdcb87abb1a9362a4c6a884031.png";

const TypingIndicator = () => (
  <div className="flex items-start gap-3 animate-fade-in">
    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md bg-pink-100">
      <img src={BOT_AVATAR} alt="Lisa" className="w-full h-full object-cover" />
    </div>
    <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm px-4 py-3">
      <div className="flex gap-1.5">
        <span className="typing-dot w-2 h-2 bg-pink-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-pink-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-pink-400 rounded-full"></span>
      </div>
    </div>
  </div>
);

// Helper function to render text with **bold** markdown support
const renderMessageContent = (content) => {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const ChatMessage = ({ message, index }) => {
  const isBot = message.role === "assistant";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`flex items-start gap-3 ${isBot ? "" : "flex-row-reverse"}`}
      data-testid={`v2-chat-message-${message.role}-${index}`}
    >
      {isBot && (
        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md bg-pink-100">
          <img src={BOT_AVATAR} alt="Lisa" className="w-full h-full object-cover" />
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-3 shadow-sm ${
          isBot
            ? "bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm"
            : "bg-gradient-to-br from-pink-500 to-orange-500 text-white rounded-2xl rounded-tr-sm"
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{renderMessageContent(message.content)}</p>
      </div>
    </motion.div>
  );
};

// Micro-Commitment Steps – 7-8 kleine Ja's
// Each step has a question, options, and intro_after for the next step
const MICRO_STEPS = {
  step1_open: {
    question: "Klingt das spannend für dich? 👀",
    options: [
      { text: "Ja, lass hören!", value: "Ja, lass hören!" },
      { text: "Mhm, vielleicht...", value: "Mhm, vielleicht..." },
    ],
  },
  step2_people: {
    question: "Würdest du gerne mit Menschen arbeiten – mal quatschen, mal überzeugen, mal lachen?",
    options: [
      { text: "Voll, das liegt mir!", value: "Voll, das liegt mir!" },
      { text: "Bin eher schüchtern", value: "Bin eher schüchtern" },
    ],
  },
  step3_money: {
    question: "Wären **2.688 € bis 4.927 € in 4 Wochen** für dich interessant?",
    options: [
      { text: "Definitiv!", value: "Definitiv!" },
      { text: "Klingt fast zu gut", value: "Klingt fast zu gut" },
    ],
  },
  step4_travel: {
    question: "Wärst du grundsätzlich offen, **3-4 Wochen am Stück** mit dem Team unterwegs zu sein?",
    options: [
      { text: "Ja, klingt nach Abenteuer!", value: "Ja, klingt nach Abenteuer!" },
      { text: "Hm, ist viel auf einmal", value: "Hm, ist viel auf einmal" },
    ],
  },
  step5_perks: {
    question: "Wir zahlen **Unterkunft & Fahrt komplett**, du bist im Team und hast danach frei. Klingt das fair?",
    options: [
      { text: "Ja, mega fair!", value: "Ja, mega fair!" },
      { text: "Was ist mit Essen?", value: "Was ist mit Essen?" },
    ],
  },
  step6_team: {
    question: "Würdest du gerne in einem **coolen Team von 8-12 Leuten** arbeiten – mit Leuten, die danach oft zu Freunden werden?",
    options: [
      { text: "Ja, klingt geil!", value: "Ja, klingt geil!" },
      { text: "Bin lieber Einzelkämpfer", value: "Bin lieber Einzelkämpfer" },
    ],
  },
  step7_start: {
    question: "Wäre **jetzt oder in 2-4 Wochen** ein guter Startzeitpunkt für dich?",
    options: [
      { text: "Sofort / Diese Woche", value: "Sofort / Diese Woche" },
      { text: "In 2 Wochen", value: "In 2 Wochen" },
      { text: "In 1 Monat", value: "In 1 Monat" },
      { text: "Noch unklar", value: "Noch unklar" },
    ],
  },
  step8_final: {
    question: "Letzter Schritt: Wenn du jetzt kurz deine Daten einträgst, melden wir uns **heute oder morgen** für einen kurzen Video-Call. **Wenn alles passt → Vertrag.** Bist du dabei?",
    options: [
      { text: "Ja, bin dabei!", value: "Ja, bin dabei!" },
      { text: "Erstmal mehr Infos", value: "Erstmal mehr Infos" },
    ],
  },
};

// Reactions to "Yes/Strong yes" and "No/Hesitation" answers
// Returns: { reaction: string, isObjection: boolean }
const getReactionText = (stepId, answerValue) => {
  // Strong objection / hesitation responses with active objection handling
  const OBJECTIONS = {
    "Mhm, vielleicht...": "Verständlich – ich war auch erst skeptisch 😊\nLass mich dir einfach mal das Wichtigste in 2 Min zeigen, dann entscheidest du.",
    "Bin eher schüchtern": "**Same hier!** Ich war richtig schüchtern als ich angefangen hab. Nach 3 Tagen im Team kennt man sich, nach einer Woche ist man Familie. Das wächst von selbst rein. 💪",
    "Klingt fast zu gut": "Ich versteh dich – sind aber echte Zahlen, kein Marketing 😊\n**Viele aus unserem Team** verdienen regelmäßig in diesem Bereich. Die, die reinhauen, schaffen auch die oberen Zahlen. Schau gerne auf factoryhype.de.",
    "Hm, ist viel auf einmal": "Stimmt erstmal! Aber: Du wohnst kostenlos, isst zusammen, lernst neue Städte kennen – die 4 Wochen fliegen. Und danach bist du **richtig stolz** auf dich. 🔥",
    "Was ist mit Essen?": "Gute Frage 😊 Frühstück ist **immer dabei**, fürs restliche Essen gibt's eine **Tagespauschale**. Du musst dir um nichts Sorgen machen.",
    "Bin lieber Einzelkämpfer": "Verstehe! Ehrlich: Auf Tour bist du nie wirklich allein – aber jeder hat seinen Freiraum. Abends entscheidest du selbst, ob du chillen oder mit dem Team rausgehen willst.",
    "Noch unklar": "Kein Stress! Wir können ja erstmal nur kurz telefonieren – **ohne Verpflichtung**. Dann hast du alle Infos und entscheidest in Ruhe.",
    "Erstmal mehr Infos": "Fair! Genau dafür ist der Video-Call da – **15 Min, locker, ohne Verpflichtung**. Wir klären alles, was offen ist. Lass uns das einfach mal terminieren?",
  };

  const POSITIVE_REACTIONS = {
    "Ja, lass hören!": "Geil, dann legen wir los! 🚀",
    "Voll, das liegt mir!": "**Same!** Genau das, was wir suchen 💯",
    "Definitiv!": "Sehr cool! Das ist auch fair für die Arbeit, die du machst.",
    "Ja, klingt nach Abenteuer!": "Genau die Einstellung 🔥 Es ist wirklich ein Abenteuer.",
    "Ja, mega fair!": "Finde ich auch! Bei uns gibt's keine versteckten Kosten – alles transparent.",
    "Ja, klingt geil!": "**Mega!** Das Team ist wirklich das Beste am ganzen Job.",
    "Sofort / Diese Woche": "Top, dann hauen wir's raus! 🚀",
    "In 2 Wochen": "Super Timing! Die nächste Tour startet bald.",
    "In 1 Monat": "Passt perfekt – haben Plätze für die übernächste Tour.",
    "Ja, bin dabei!": "**Mega!** Dann lass uns das jetzt fix machen 🎉",
  };

  if (OBJECTIONS[answerValue]) return { reaction: OBJECTIONS[answerValue], isObjection: true };
  if (POSITIVE_REACTIONS[answerValue]) return { reaction: POSITIVE_REACTIONS[answerValue], isObjection: false };
  return { reaction: "Cool!", isObjection: false };
};

// Social proof + scarcity stingers between steps (general, no specific cities)
const SOCIAL_PROOF_STINGERS = [
  "👀 **Letzte Woche** haben sich dutzende neue Bewerber gemeldet – die Slots gehen schnell.",
  "🔥 **Aktuell** sind nur noch wenige Plätze frei.",
  "💬 **Viele aus unserem Team** waren erst skeptisch – jetzt sind sie regelmäßig auf Tour.",
];

const FLOW_ORDER = [
  "step1_open",
  "step2_people",
  "step3_money",
  "step4_travel",
  "step5_perks",
  "step6_team",
  "step7_start",
  "step8_final",
];

export default function FactoryHypeV2ChatPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContactButton, setShowContactButton] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [currentStep, setCurrentStep] = useState(null); // currently shown micro-step

  // Voice
  const [voiceSettings, setVoiceSettings] = useState({ enabled: false, allow_user_choice: false, voice_mandatory: false, voice_mandatory_text: "", max_duration_seconds: 30, prompt_text: "" });
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [voiceCompleted, setVoiceCompleted] = useState(false);
  const [showVoiceChoiceButtons, setShowVoiceChoiceButtons] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      }, 150);
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Greeting – emotional hook with persona story (no specific cities)
  const buildGreeting = () => ({
    role: "assistant",
    content: `Hey, hier ist **Lisa** 👋 – ich leite eines der Teams bei FactoryHype.

**Vor 2 Jahren** saß ich genau dort, wo du jetzt sitzt. War mir damals auch unsicher. Heute war ich in **vielen Städten** unterwegs, hab meine besten Freunde dabei kennengelernt und mache einen Job, hinter dem ich wirklich stehe. ✨

Wir machen Promotion für **WWF**, **SOS-Kinderdörfer** und **Tierschutzprojekte** – also Sachen, die wirklich was bringen. Im Team. Mit Reisen. Und mit **richtig gutem Geld**: 2.688 € bis 4.927 € in 4 Wochen.

Klingt das spannend für dich? 👀`,
  });

  const initChat = async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    try {
      const sessionResponse = await axios.post(`${API}/chat/session?project_id=${PROJECT_ID}`);
      setSessionId(sessionResponse.data.session_id);

      try {
        const voiceResponse = await axios.get(`${API}/voice-settings/${PROJECT_ID}`);
        setVoiceSettings(voiceResponse.data);
      } catch (err) {
        // voice settings optional
      }

      setMessages([buildGreeting()]);
      setCurrentStep("step1_open");
    } catch (err) {
      console.error("Init error", err);
      setMessages([buildGreeting()]);
      setCurrentStep("step1_open");
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMessageToBackend = async (message) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/chat/save-message`, {
        session_id: sessionId,
        project_id: PROJECT_ID,
        message,
      });
    } catch (err) {
      console.error("Error saving message", err);
    }
  };

  const proceedToContactForm = () => {
    if (voiceSettings.enabled && !voiceCompleted) {
      if (voiceSettings.voice_mandatory) {
        const mandatoryText = voiceSettings.voice_mandatory_text || "Bevor's losgeht: Nimm bitte kurz eine Sprachnachricht auf. 2-3 Sätze, warum genau du der/die Richtige bist – das hebt dich aus der Masse raus.";
        setMessages((prev) => [...prev, { role: "assistant", content: mandatoryText }]);
        setShowVoiceRecorder(true);
        setShowContactButton(false);
        setShowVoiceChoiceButtons(false);
      } else if (voiceSettings.allow_user_choice) {
        setShowVoiceChoiceButtons(true);
        setShowContactButton(false);
        setShowVoiceRecorder(false);
      } else {
        setShowVoiceRecorder(true);
        setShowContactButton(false);
        setShowVoiceChoiceButtons(false);
      }
    } else {
      setShowContactButton(true);
      setShowVoiceRecorder(false);
      setShowVoiceChoiceButtons(false);
    }
  };

  const handleVoiceChoice = (choice) => {
    setShowVoiceChoiceButtons(false);
    if (choice === "voice") {
      setShowVoiceRecorder(true);
      setShowContactButton(false);
    } else {
      setVoiceCompleted(true);
      setShowVoiceRecorder(false);
      setShowContactButton(false);
      setIsContactModalOpen(true);
    }
  };

  const handleVoiceComplete = async () => {
    setVoiceCompleted(true);
    setShowVoiceRecorder(false);

    const userVoiceMessage = { role: "user", content: "🎤 Sprachnachricht aufgenommen" };
    setMessages((prev) => [...prev, userVoiceMessage]);
    await saveMessageToBackend(userVoiceMessage);

    const confirmMessage = {
      role: "assistant",
      content: `**Mega, danke dir!** 🎤\n\nJetzt nur noch **schnell die Kontaktdaten** ausfüllen – damit ist deine Bewerbung komplett im System und ich rufe dich heute oder morgen persönlich an.`,
    };
    setMessages((prev) => [...prev, confirmMessage]);
    await saveMessageToBackend(confirmMessage);
    setShowContactButton(true);
  };

  const advanceStep = async (currentStepId, answerValue) => {
    const currentIndex = FLOW_ORDER.indexOf(currentStepId);
    const nextStepId = FLOW_ORDER[currentIndex + 1];

    const { reaction, isObjection } = getReactionText(currentStepId, answerValue);

    let combinedText = reaction;

    // Optional: stinger between certain steps to add social proof / scarcity
    if (!isObjection && currentStepId === "step3_money") {
      combinedText += `\n\n${SOCIAL_PROOF_STINGERS[0]}`;
    }
    if (!isObjection && currentStepId === "step5_perks") {
      combinedText += `\n\n${SOCIAL_PROOF_STINGERS[1]}`;
    }

    if (!nextStepId) {
      // End of flow → contact form
      const finalMsg = {
        role: "assistant",
        content: `${combinedText}\n\n**Letzter Schritt:** Trag jetzt kurz deine Daten ein – ich melde mich **heute oder morgen persönlich** bei dir. 💚`,
      };
      setMessages((prev) => [...prev, finalMsg]);
      try {
        await axios.post(`${API}/chat/quali-message`, {
          session_id: sessionId,
          user_message: answerValue,
          bot_response: finalMsg.content,
        });
      } catch (e) {
        // best effort
      }
      proceedToContactForm();
      return;
    }

    const nextStep = MICRO_STEPS[nextStepId];
    const nextMsg = {
      role: "assistant",
      content: `${combinedText}\n\n**${nextStep.question}**`,
    };
    setMessages((prev) => [...prev, nextMsg]);
    setCurrentStep(nextStepId);
    try {
      await axios.post(`${API}/chat/quali-message`, {
        session_id: sessionId,
        user_message: answerValue,
        bot_response: nextMsg.content,
      });
    } catch (e) {
      // best effort
    }
  };

  const handleStepButtonClick = async (answerValue) => {
    if (!currentStep) return;
    const stepBeingAnswered = currentStep;
    setCurrentStep(null); // hide buttons immediately
    const userMessage = { role: "user", content: answerValue };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    try {
      await advanceStep(stepBeingAnswered, answerValue);
    } finally {
      setIsLoading(false);
    }
  };

  const sendFreeText = async () => {
    const text = inputValue.trim();
    if (!text || isLoading || !sessionId) return;
    setInputValue("");
    const textLower = text.toLowerCase();
    const userMessage = { role: "user", content: text };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // --- 1. WICHTIG: Wenn Micro-Step aktiv, versuche die Antwort einer Button-Option zuzuordnen
    const stepActive = currentStep;
    if (stepActive && MICRO_STEPS[stepActive]) {
      const options = MICRO_STEPS[stepActive].options;
      let matchedValue = null;
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const optLower = opt.text.toLowerCase();
        const isPositive = i === 0;
        const isNegative = i === 1;
        if (
          textLower === optLower ||
          textLower.includes(optLower) ||
          optLower.includes(textLower) ||
          // Synonyme für "JA / positive Antwort"
          (isPositive && (
            textLower === "ja" ||
            (textLower.includes("ja") && textLower.length < 20) ||
            textLower.includes("klar") ||
            textLower.includes("natürlich") ||
            textLower.includes("natuerlich") ||
            textLower.includes("passt") ||
            textLower.includes("sicher") ||
            textLower.includes("klingt gut") ||
            textLower.includes("gerne") ||
            textLower.includes("definitiv")
          )) ||
          // Synonyme für "NEIN / negative Antwort"
          (isNegative && (
            textLower === "nein" ||
            (textLower.includes("nein") && textLower.length < 20) ||
            textLower.includes("nicht so") ||
            textLower.includes("eher nicht") ||
            textLower.includes("lieber nicht")
          ))
        ) {
          matchedValue = opt.value;
          break;
        }
      }

      if (matchedValue) {
        // Behandle als Button-Klick – setze isLoading zurück und delegiere
        setIsLoading(false);
        // Update currentStep BEFORE calling handleStepButtonClick (to avoid race)
        await handleStepButtonClickFromFreeText(matchedValue, stepActive);
        return;
      }
    }

    // --- 2. Andernfalls: FAQ-Mode über LLM, mit Wiederholung der aktuellen Frage
    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        faq_mode: true,
      });
      let answerText = response.data.response;
      if (stepActive && MICRO_STEPS[stepActive]) {
        answerText += `\n\n**Aber zurück zu meiner Frage:** ${MICRO_STEPS[stepActive].question}`;
      }
      const botMessage = { role: "assistant", content: answerText };
      setMessages((prev) => [...prev, botMessage]);
      // Re-save the enriched answer so admin/log sees the full message
      await saveMessageToBackend(botMessage);
    } catch (err) {
      console.error(err);
      let fallback = "Gute Frage! 😊 Das klären wir am besten kurz im Video-Call – da kann ich dir alles persönlich erklären.";
      if (stepActive && MICRO_STEPS[stepActive]) {
        fallback += `\n\n**Aber zurück zu meiner Frage:** ${MICRO_STEPS[stepActive].question}`;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: fallback }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper: called from sendFreeText when matched an option value
  const handleStepButtonClickFromFreeText = async (answerValue, stepBeingAnswered) => {
    setCurrentStep(null);
    setIsLoading(true);
    try {
      await advanceStep(stepBeingAnswered, answerValue);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendFreeText();
    }
  };

  const handleContactSubmit = async (formData) => {
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        session_id: sessionId,
        project_id: PROJECT_ID,
      });
      setIsSubmitted(true);
      setIsContactModalOpen(false);
      const thankYou = {
        role: "assistant",
        content: "**Mega, danke!** 🙌\n\nDeine Bewerbung ist drin. Ich rufe dich **heute oder morgen** persönlich an – wir freuen uns auf dich!\n\nBis gleich 💚🔥",
      };
      setMessages((prev) => [...prev, thankYou]);
    } catch (err) {
      console.error("contact submit", err);
    }
  };

  const resetChat = async () => {
    initializedRef.current = false;
    setMessages([]);
    setSessionId(null);
    setShowContactButton(false);
    setIsSubmitted(false);
    setVoiceCompleted(false);
    setShowVoiceRecorder(false);
    setShowVoiceChoiceButtons(false);
    setCurrentStep(null);
    setIsInitializing(true);
    await initChat();
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-pink-100 mx-auto mb-4 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-pink-600 animate-pulse" />
          </div>
          <p className="text-pink-600 font-medium">Lisa kommt gleich...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-pink-50 to-orange-50 flex flex-col overflow-hidden" data-testid="factoryhype-v2-chat-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-pink-500 to-orange-500 text-white py-2.5 px-3 shadow-lg">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/30 bg-pink-100 flex-shrink-0">
            <img src={BOT_AVATAR} alt="Lisa" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold flex items-center gap-1.5 leading-tight">
              <span className="truncate">FactoryHype</span>
              <Flame className="w-3.5 h-3.5 text-orange-100 flex-shrink-0" />
            </h1>
            <p className="text-[11px] text-pink-50 truncate">Mit Lisa zum Job mit Sinn</p>
          </div>
          <button
            onClick={() => setIsContactModalOpen(true)}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg transition-colors shadow-sm flex-shrink-0 whitespace-nowrap ${
              showContactButton && !isSubmitted
                ? "bg-yellow-300 text-pink-700 animate-pulse hover:bg-yellow-200"
                : "bg-white text-pink-700 hover:bg-pink-50"
            }`}
            data-testid="v2-apply-button"
          >
            <Briefcase className="w-3 h-3" />
            <span>Sichern</span>
          </button>
          <button
            onClick={resetChat}
            className="flex items-center gap-1 px-1.5 py-1 text-xs text-pink-50 hover:text-white hover:bg-pink-600 rounded-lg transition-colors flex-shrink-0"
            title="Chat neu starten"
            data-testid="v2-reset-chat-button"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="h-20 flex-shrink-0"></div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="v2-chat-scroll-area">
        <div className="max-w-2xl mx-auto space-y-4 pb-4">
          {messages.map((message, index) => (
            <ChatMessage key={index} message={message} index={index} />
          ))}

          {/* Micro-step buttons */}
          {currentStep && MICRO_STEPS[currentStep] && !isLoading && (
            <div className="flex flex-wrap gap-2 mt-3">
              {MICRO_STEPS[currentStep].options.map((option, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => handleStepButtonClick(option.value)}
                  className="text-xs border-pink-200 hover:bg-pink-50 hover:text-pink-700"
                  data-testid={`v2-step-btn-${currentStep}-${idx}`}
                >
                  {option.text}
                </Button>
              ))}
            </div>
          )}

          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-slate-200 p-4 shadow-lg">
        {showVoiceChoiceButtons && (
          <div className="max-w-2xl mx-auto mb-4">
            <div className="bg-pink-50 border border-pink-200 rounded-xl p-4">
              <p className="text-center text-slate-700 mb-2 font-medium">**Mega, du bist fast durch!** 🎉</p>
              <p className="text-center text-slate-600 text-sm mb-4">
                Eine kurze Sprachnachricht hebt dich aus der Masse raus – Bewerbungen mit Voice werden bei uns <strong>oft bevorzugt angeschaut</strong>.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => handleVoiceChoice("voice")}
                  className="w-full rounded-xl bg-pink-600 hover:bg-pink-700 text-white py-3"
                  data-testid="v2-voice-choice-voice"
                >
                  🎤 Kurz Sprachnachricht senden
                </Button>
                <Button
                  onClick={() => handleVoiceChoice("contact")}
                  variant="outline"
                  className="w-full rounded-xl border-pink-400 text-pink-700 hover:bg-pink-50 py-3"
                  data-testid="v2-voice-choice-contact"
                >
                  ⌨️ Direkt Kontaktdaten eintragen
                </Button>
              </div>
            </div>
          </div>
        )}

        {showVoiceRecorder && (
          <div className="max-w-2xl mx-auto mb-4">
            <VoiceRecorder
              sessionId={sessionId}
              projectId={PROJECT_ID}
              maxDuration={voiceSettings.max_duration_seconds || 30}
              promptText={voiceSettings.prompt_text}
              onComplete={handleVoiceComplete}
            />
          </div>
        )}

        {showContactButton && !isSubmitted && !showVoiceRecorder && (
          <div className="max-w-2xl mx-auto mb-3">
            <Button
              onClick={() => setIsContactModalOpen(true)}
              className="w-full rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 hover:from-pink-700 hover:to-orange-600 text-white py-3 font-bold"
              data-testid="v2-contact-button"
            >
              📝 Kontaktdaten eintragen & Job sichern
            </Button>
          </div>
        )}

        <div className="max-w-2xl mx-auto flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Frag mich alles... 💬"
            className="flex-1 rounded-xl border-pink-200 focus:border-pink-400 focus:ring-pink-400"
            disabled={isLoading}
            data-testid="v2-chat-input"
          />
          <Button
            onClick={sendFreeText}
            disabled={!inputValue.trim() || isLoading}
            className="rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 hover:from-pink-700 hover:to-orange-600"
            data-testid="v2-send-button"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>

        <div className="max-w-2xl mx-auto mt-3 px-4 text-center">
          <span className="text-[10px] text-slate-400 leading-tight">
            Made by FactoryHype · KI-gestützter Sales-Chat zur ersten Orientierung · Verbindliche Infos im persönlichen Gespräch · Deine Daten werden vertraulich verarbeitet.
          </span>
        </div>
      </div>

      <FactoryHypeContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={handleContactSubmit}
        sessionId={sessionId}
      />
    </div>
  );
}
